<template>
  <div class="container-fluid p-4">
    <!-- Welcome Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="fade-in">
          <h2 class="mb-2 fw-bold gradient-text">{{ getWelcomeMessage() }}</h2>
          <p class="text-muted">{{ getSubtitleMessage() }}</p>
        </div>
      </div>
    </div>

    <!-- Child Selection Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <h3 class="mb-0 fw-bold" style="color: var(--text-primary);">Parent Dashboard</h3>
          <div class="d-flex align-items-center gap-2">
            <select
              class="form-select header-ctrl"
              style="max-width: 260px; border: 2px solid var(--border-color); border-radius: 8px;"
              v-model="selectedChildId"
              @change="onChildChange"
            >
              <option :value="null">Select Child...</option>
              <option
                v-for="c in children"
                :key="c.id"
                :value="c.id"
              >
                {{ c.full_name }}
              </option>
            </select>
            <button class="btn btn-success" style="border-radius: 20px;" @click="openLinkChildModal">
              <i class="bi bi-person-plus me-1"></i> Add Child
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Child Overview Card -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div 
                class="me-4"
                style="width: 60px; height: 60px; background: #3b82f6; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: 700;"
              >
                👤
              </div>
              <div>
                <h5 class="mb-1 fw-bold" style="color: #1e40af;">{{ childInfo.full_name || 'Child' }}</h5>
                <p class="mb-0" style="color: #3b82f6;">{{ childInfo.grade || '' }}</p>
              </div>
            </div>
            <div class="text-center">
              <div class="progress-ring" style="width: 80px; height: 80px;">
                <div class="progress-ring-circle">
                  <div class="progress-ring-fill" :class="{ full: ringPercent >= 100 }" :style="{ transform: ringTransform }"></div>
                </div>
                <div class="progress-ring-text" style="color: #3b82f6; font-size: 16px;">{{ ringPercent }}%</div>
              </div>
              <p class="small mb-0 mt-2" style="color: #3b82f6;">Tasks Completed</p>
            </div>
            <button class="btn" style="background: #f97316; color: white; border-radius: 20px;">
              🔔 Send Reminder
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="row g-4">
      <!-- Recent Activity -->
      <div class="col-lg-6">
        <div class="modern-card p-4 h-100" style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);">
          <h5 class="mb-4 fw-bold" style="color: #92400e;">Recent Activity</h5>
          <div class="space-y-3">
            <div v-if="!recentActivities.length" class="p-3 rounded-3 text-center" style="background: var(--bg-secondary); color: var(--text-secondary);">No recent activity this week.</div>
            <div 
              v-for="(activity, index) in recentActivities" 
              :key="index" 
              class="d-flex align-items-center p-3 rounded-3 mb-3" 
              style="background: var(--bg-secondary);"
            >
              <div class="me-3" style="font-size: 20px;">{{ activity.icon }}</div>
              <div class="flex-grow-1">
                <h6 class="mb-1" style="color: var(--text-primary);">{{ activity.text }}</h6>
                <small style="color: var(--text-secondary);">{{ formatWhen(activity.time) }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- This Week's Tasks -->
      <div class="col-lg-6">
        <div class="modern-card p-4 h-100" style="background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);">
          <h5 class="mb-4 fw-bold" style="color: #065f46;">This Week's Tasks</h5>
          <div class="row g-3">
            <div 
              v-for="task in weeklyTasks" 
              :key="task.id" 
              class="col-12"
            >
              <div class="d-flex align-items-center justify-content-between p-3 rounded-3" style="background: var(--bg-secondary);">
                <div class="d-flex align-items-center">
                  <div 
                    class="me-3"
                    style="width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px;"
                    :style="{ background: task.completed ? '#10b981' : '#f59e0b', color: 'white' }"
                  >
                    <i :class="task.completed ? 'bi-check-lg' : 'bi-clock'"></i>
                  </div>
                  <div>
                    <h6 class="mb-0" style="color: var(--text-primary);">{{ task.title }}</h6>
                    <small style="color: var(--text-secondary);">{{ task.subject }}</small>
                  </div>
                </div>
                <span 
                  class="badge px-3 py-1"
                  :style="{ 
                    background: task.completed ? '#10b981' : '#f59e0b', 
                    color: 'white', 
                    borderRadius: '20px' 
                  }"
                >
                  {{ task.completed ? 'Completed' : 'Pending' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Encouragement Section -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <h5 class="mb-2 fw-bold" style="color: #0e7490;">Keep Encouraging! 👏</h5>
              <p class="mb-3" style="color: #0891b2;">Check your child's progress and send positive feedback.</p>
                 <div class="row g-4">
                <div class="col-md-4 text-center">
                  <div class="p-3">
                    <h4 class="fw-bold" style="color: var(--text-primary);">{{ metrics.tasksCompleted }}</h4>
                    <p class="mb-0 small" style="color: var(--text-secondary);">Tasks Completed</p>
                  </div>
                </div>
                <div class="col-md-4 text-center">
                  <div class="p-3">
                    <h4 class="fw-bold" style="color: var(--text-primary);">{{ metrics.skillsImproved }}</h4>
                    <p class="mb-0 small" style="color: var(--text-secondary);">Skills Improved</p>
                  </div>
                </div>
                <div class="col-md-4 text-center">
                  <div class="p-3">
                    <h4 class="fw-bold" style="color: var(--text-primary);">{{ metrics.badgesEarned }}</h4>
                    <p class="mb-0 small" style="color: var(--text-secondary);">Badges Earned</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-4 text-end">
              <div class="d-flex gap-3 justify-content-end">
                <button class="btn" style="background: #06b6d4; color: white; border-radius: 20px;" @click="openMessageModal">
                  💬 Send Message
                </button>
            <button class="btn" style="background: #f97316; color: white; border-radius: 20px;" @click="openBadgeModal">
              🎁 Create Custom Badge
            </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Send Message Modal -->
    <div v-if="showMessageModal" class="modal fade show" style="display:block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Send Message</h5>
            <button type="button" class="btn-close" @click="closeMessageModal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Recipient</label>
                <select class="form-select" v-model="messageRecipient">
                  <option value="child">Child</option>
                  <option value="teacher">Teacher</option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label">Subject</label>
                <input class="form-control" v-model.trim="messageSubject" placeholder="Subject" />
              </div>
              <div class="col-12">
                <label class="form-label">Message</label>
                <textarea class="form-control" rows="3" v-model.trim="messageBody" placeholder="Type your message..."></textarea>
              </div>
              <div v-if="messageError" class="col-12">
                <div class="alert alert-danger">{{ messageError }}</div>
              </div>
              <div v-if="messageSuccess" class="col-12">
                <div class="alert alert-success">{{ messageSuccess }}</div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeMessageModal">Close</button>
            <button class="btn btn-primary" :disabled="sendingMessage" @click="submitMessage">
              <span v-if="sendingMessage" class="spinner-border spinner-border-sm me-2"></span>
              Send
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Custom Badge Modal -->
    <div v-if="showBadgeModal" class="modal fade show" style="display:block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create Custom Badge</h5>
            <button type="button" class="btn-close" @click="closeBadgeModal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Title</label>
                <input class="form-control" v-model="newBadge.title" placeholder="e.g., Reading Star" />
              </div>
              <div class="col-12">
                <label class="form-label">Description</label>
                <textarea class="form-control" rows="2" v-model="newBadge.description"></textarea>
              </div>
              <div class="col-md-6">
                <label class="form-label">Icon</label>
                <input class="form-control" v-model="newBadge.icon" placeholder="e.g., 🏅" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Difficulty</label>
                <select class="form-select" v-model="newBadge.difficulty">
                  <option>Beginner</option>
                  <option>Intermediate</option>
                  <option>Advanced</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Required Count</label>
                <input type="number" class="form-control" v-model.number="newBadge.required" min="1" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Category</label>
                <input class="form-control" v-model="newBadge.category" placeholder="e.g., Reading" />
              </div>
            </div>
            <div v-if="badgeCreateError" class="alert alert-danger mt-3">{{ badgeCreateError }}</div>
            <div v-if="badgeCreateMessage" class="alert alert-success mt-3">{{ badgeCreateMessage }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeBadgeModal">Close</button>
            <button class="btn btn-primary" :disabled="creatingBadge" @click="submitCreateBadge">
              <span v-if="creatingBadge" class="spinner-border spinner-border-sm me-2"></span>
              Create Badge
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Link Child Modal (by Email) -->
    <div v-if="showLinkChild" class="modal fade show" style="display:block; background: rgba(0,0,0,0.5);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Link Child by Email</h5>
            <button type="button" class="btn-close" @click="closeLinkChildModal"></button>
          </div>
          <div class="modal-body">
            <label class="form-label">Child's Account Email</label>
            <input class="form-control" v-model.trim="linkChildEmail" placeholder="child@example.com" />
            <div v-if="linkChildError" class="alert alert-danger mt-3">{{ linkChildError }}</div>
            <div v-if="linkChildMessage" class="alert alert-success mt-3">{{ linkChildMessage }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeLinkChildModal">Close</button>
            <button class="btn btn-primary" :disabled="linkingChild" @click="submitLinkChild">
              <span v-if="linkingChild" class="spinner-border spinner-border-sm me-2"></span>
              Link Child
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ParentDashboard',
  props: {
    user: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      currentUser: null,
      selectedChildId: null,
      children: [],
      childInfo: {},
      recentActivities: [],
      weeklyTasks: [],
      metrics: { tasksCompleted: 0, skillsImproved: 0, badgesEarned: 0 },
      ringPercent: 0,
      // Badge modal
      showBadgeModal: false,
      creatingBadge: false,
      badgeCreateError: '',
      badgeCreateMessage: '',
      newBadge: { title: '', description: '', icon: '🏅', difficulty: 'Beginner', required: 1, category: '' },
      // Link child modal state
      showLinkChild: false,
      linkingChild: false,
      linkChildEmail: '',
      linkChildError: '',
      linkChildMessage: '',
      // Message modal state
      showMessageModal: false,
      messageRecipient: 'child',
      messageSubject: '',
      messageBody: '',
      sendingMessage: false,
      messageError: '',
      messageSuccess: ''
    }
  },
  mounted() {
    // Get current user from localStorage or props
    const storedUser = localStorage.getItem('currentUser')
    if (storedUser) {
      this.currentUser = JSON.parse(storedUser)
    } else if (this.user) {
      this.currentUser = this.user
    }
    this.loadChildren()
  },
  methods: {
    async loadChildren() {
      try {
        const { getChildren } = await import('../services/parentService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const parentId = user?.id || 0
        const res = await getChildren(parentId)
        if (res?.success) {
          this.children = res.data || []
          this.selectedChildId = this.children[0]?.id || null
          if (this.selectedChildId) await this.fetchChildProgress()
        }
      } catch (e) {
        console.error('Failed to load children:', e)
      }
    },
    async fetchChildProgress() {
      try {
        const { getChildProgress } = await import('../services/parentService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const parentId = user?.id || 0
        const res = await getChildProgress(parentId, this.selectedChildId)
        if (res?.success) {
          const d = res.data
          this.childInfo = {
            full_name: d.full_name,
            grade: d.grade,
            points: d.points,
            level: d.level,
            streak: d.streak
          }
          // Map tasks into weeklyTasks (status → completed)
          this.weeklyTasks = (d.task_progress || []).map(t => ({
            id: t.id,
            title: t.title,
            subject: 'Task',
            completed: (t.status || '').toLowerCase() === 'completed'
          }))
          // Recent activity feed
          this.recentActivities = Array.isArray(d.recent_activity) ? d.recent_activity : []
          // Simple metrics derived from tasks
          this.metrics.tasksCompleted = this.weeklyTasks.filter(t => t.completed).length
          this.metrics.badgesEarned = 0
          this.metrics.skillsImproved = 0
          this.ringPercent = this.weeklyTasks.length ? Math.round((this.metrics.tasksCompleted / this.weeklyTasks.length) * 100) : 0
        }
      } catch (e) {
        console.error('Failed to load child progress:', e)
      }
    },
    onChildChange() {
      if (this.selectedChildId) this.fetchChildProgress()
    },
    openBadgeModal() {
      this.badgeCreateError = ''
      this.badgeCreateMessage = ''
      this.showBadgeModal = true
    },
    closeBadgeModal() {
      this.showBadgeModal = false
    },
    openLinkChildModal() {
      this.linkChildEmail = ''
      this.linkChildError = ''
      this.linkChildMessage = ''
      this.showLinkChild = true
    },
    closeLinkChildModal() {
      this.showLinkChild = false
    },
    // Message modal handlers
    openMessageModal() {
      this.messageError = ''
      this.messageSuccess = ''
      this.messageRecipient = 'child'
      this.messageSubject = ''
      this.messageBody = ''
      this.showMessageModal = true
    },
    closeMessageModal() {
      this.showMessageModal = false
    },
    async submitMessage() {
      try {
        this.messageError = ''
        this.messageSuccess = ''
        if (!this.currentUser?.id) {
          this.messageError = 'Missing parent id'
          return
        }
        if (!this.selectedChildId) {
          this.messageError = 'Please select a child first'
          return
        }
        if (!this.messageBody) {
          this.messageError = 'Please enter a message'
          return
        }
        this.sendingMessage = true
        // Determine receiver id
        let receiverId = this.selectedChildId
        if (this.messageRecipient === 'teacher') {
          try {
            // Future: pick class teacher; fallback to child to ensure delivery
            receiverId = this.selectedChildId
          } catch (e) {
            receiverId = this.selectedChildId
          }
        }
        const base = (process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000').replace(/\/$/, '')
        const token = localStorage.getItem('authToken')
        const res = await fetch(`${base}/api/messages/${this.currentUser.id}/${receiverId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authentication-Token': token, 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ subject: this.messageSubject || 'Message from Parent', content: this.messageBody })
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.error || 'Failed to send message')
        }
        this.messageSuccess = 'Message sent!'
        try {
          const { default: toast } = await import('../services/toastService.js')
          toast.success('Message sent')
        } catch (err) {
          // Ignore toast initialization errors in environments without Bootstrap
          void err
        }
        this.showMessageModal = false
      } catch (e) {
        this.messageError = e?.message || 'Failed to send message'
      } finally {
        this.sendingMessage = false
      }
    },
    async submitCreateBadge() {
      try {
        this.badgeCreateError = ''
        this.badgeCreateMessage = ''
        if (!this.currentUser?.id) {
          this.badgeCreateError = 'Missing parent id'
          return
        }
        if (!this.newBadge.title) {
          this.badgeCreateError = 'Title is required'
          return
        }
        this.creatingBadge = true
        const { createCustomBadge } = await import('../services/badgeService.js')
        const payload = {
          title: this.newBadge.title,
          icon: this.newBadge.icon,
          description: this.newBadge.description,
          category: this.newBadge.category || 'custom',
          difficulty: this.newBadge.difficulty || 'Beginner',
          required: this.newBadge.required || 1,
          creator_id: this.currentUser.id
        }
        // Create badge for the selected child (if any); else create global custom badge
        const targetUserId = this.selectedChildId || this.currentUser.id
        await createCustomBadge(this.currentUser.id, targetUserId, payload)
        this.badgeCreateMessage = 'Badge created! It will appear in the child\'s locked badges.'
        this.newBadge = { title: '', description: '', icon: '🏅', difficulty: 'Beginner', required: 1, category: '' }
      } catch (e) {
        console.error('Create badge failed:', e)
        this.badgeCreateError = e?.message || 'Failed to create badge'
      } finally {
        this.creatingBadge = false
      }
    },
    async submitLinkChild() {
      try {
        this.linkChildError = ''
        this.linkChildMessage = ''
        if (!this.currentUser?.id) {
          this.linkChildError = 'Missing parent id'
          return
        }
        if (!this.linkChildEmail) {
          this.linkChildError = 'Please enter a child email'
          return
        }
        this.linkingChild = true
        const { linkChildByEmail, getChildren } = await import('../services/parentService.js')
        const res = await linkChildByEmail(this.currentUser.id, this.linkChildEmail)
        if (!res?.success) {
          // Specific friendly message for missing student email
          if ((res.error || '').toLowerCase().includes('no student account')) {
            this.linkChildError = 'No student account found for that email. Please check the email or ask your child to register first.'
          } else {
            this.linkChildError = res?.error || 'Failed to link child'
          }
          return
        }
        this.linkChildMessage = 'Child linked successfully'
        // Refresh the child list and select the child if present
        const refreshed = await getChildren(this.currentUser.id)
        if (refreshed?.success) {
          this.children = refreshed.data || []
          this.selectedChildId = this.children[0]?.id || null
          if (this.selectedChildId) await this.fetchChildProgress()
        }
        this.showLinkChild = false
      } catch (e) {
        console.error('Link child failed:', e)
        this.linkChildError = e?.message || 'Failed to link child'
      } finally {
        this.linkingChild = false
      }
    },
    getWelcomeMessage() {
      if (!this.currentUser) return 'Welcome!'
      const hour = new Date().getHours()
      const greeting = hour < 12 ? 'Good Morning' : hour < 18 ? 'Good Afternoon' : 'Good Evening'
      return `${greeting}, ${this.currentUser.name}! Here's how your child is progressing today.`
    },

    getSubtitleMessage() {
      return "Stay connected with your child's educational journey."
    },
    formatWhen(iso) {
      if (!iso) return ''
      try { return new Date(iso).toLocaleString() } catch { return '' }
    }
  },
  computed: {
    ringTransform() {
      const pct = Math.min(100, Math.max(0, this.ringPercent))
      const angle = pct >= 100 ? 360 : (pct / 100) * 360
      return `rotate(-90deg) rotate(${angle}deg)`
    }
  }
}
</script>

<style scoped>
.space-y-3 > * + * {
  margin-top: 0.75rem;
}

/* Align header select height to match teacher dashboard button */
.form-select.header-ctrl {
  height: 38px;
  display: inline-flex;
  align-items: center;
}

.form-select {
  background: var(--bg-primary);
  color: var(--text-primary);
  border-color: var(--border-color);
}

.form-select:focus {
  background: var(--bg-primary);
  color: var(--text-primary);
  border-color: var(--role-primary);
  box-shadow: 0 0 0 0.2rem rgba(var(--role-primary-rgb), 0.25);
}

.form-select option {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.progress-ring {
  position: relative;
  display: inline-block;
}

.progress-ring-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 6px solid var(--bg-secondary);
  position: relative;
  overflow: hidden;
}

.progress-ring-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 6px solid var(--role-primary);
  border-left-color: transparent;
  border-bottom-color: transparent;
}

/* When 100%, show a solid ring without gaps */
.progress-ring-fill.full {
  border-left-color: var(--role-primary);
  border-bottom-color: var(--role-primary);
}

.progress-ring-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
  font-weight: bold;
  color: var(--role-primary);
}
</style> 